import { isPast, parseISO, subWeeks } from "date-fns";
import { groupBy, isNil, isUndefined, keys, mapValues, toLower } from "lodash-es";
import every from "lodash-es/every";
import includes from "lodash-es/includes";
import isArray from "lodash-es/isArray";
import some from "lodash-es/some";
import sortBy from "lodash-es/sortBy";
import uniq from "lodash-es/uniq";
import type { ManualShortcut } from "../../common/types/categoryTypes";
import { ORDER_UNIT, type OrderUnit } from "../../common/types/productOrderTypes";
import {
   type Badge,
   type BadgeKey,
   type FilterOptionsByField,
   type FilterSettings,
   type Filters,
   hasKey,
   isFilterAttribute,
   isLabelColor,
   isLabelPosition,
   type Label,
   type LabelText,
   type LegalFilterKey,
   legalFilterKeys,
   PageType,
   type Product,
   type RawProduct,
   type ShortcutFilter
} from "../../common/types/productTypes";

/**
 * Takes a list of existing badges and adds another bedge if the product has the given attributes.
 */
const mapBadge = (
   existingBadges: Badge[],
   product: RawProduct,
   certificationValue: string,
   badgeName: string,
   badgeKey: BadgeKey
) => {
   if (isNil(product.certifications)) {
      return;
   }

   const normalizedCerts = product.certifications.map((cert) => cert?.toLowerCase() || "");

   if (includes(normalizedCerts, certificationValue.toLowerCase())) {
      existingBadges.push({ name: badgeName, key: badgeKey });
   }
};

/**
 * Inspects the attributes of the products and creates Badges for matching attributes.
 * @returns An array of badges.
 */
export const mapBadges = (product: RawProduct) => {
   const badges: Badge[] = [];
   if (product.recyclingChargeBaseUnit === 2) {
      badges.push({ name: "Pant, kr 2", key: "pant_2kr" });
   }
   if (product.recyclingChargeBaseUnit === 3) {
      badges.push({ name: "Pant, kr 3", key: "pant_3kr" });
   }
   mapBadge(badges, product, "Nyt_Norge", "Nyt Norge", "nyt_norge");
   mapBadge(badges, product, "Nokkelhullet", "Nøkkelhull", "nokkelhullet");
   return badges;
};

export const brandsInProducts = (products: Product[]): string[] => {
   const brands = new Set<string>();
   for (const product of products) {
      if (product.brand) {
         brands.add(product.brand);
      }
   }
   return Array.from(brands);
};

export const shouldShowExpiryNotice = (product: Product) =>
   !isNil(product.expiringFromDate) && product.expiringFromDate !== "" && isPast(subWeeks(parseISO(product.expiringFromDate), 6));

export const isSeasonalProduct = (product: Product) => product.labels.some((label) => label.text === "Sesong");

/**
 * Inspects the attributes of the products and creates Labels for matching attributes.
 * @returns An array of labels.
 */
export const mapLabels = (product: Product | RawProduct) => {
   const labels: Label[] = [];

   // Label: Veievare - Prio: 1
   if (product.variableWeight) {
      labels.push({
         text: "Veievare",
         color: "gray",
         position: "bottom-right",
         page: [PageType.ProductDetailsPage, PageType.ProductListPage]
      });
   }

   // Label: Siste sjanse - Prio: 3 - Show 4 weeks before expiry date (#c40076)
   // OBS! Moved to manual control in Plytix for now.

   // Label: Avtalesortiment - Prio: 3 (#554494 + tag icon)
   if ("inAgreementAssortment" in product && product.inAgreementAssortment) {
      labels.push({
         text: "Avtalesortiment",
         color: "gray",
         position: "bottom-left",
         page: [PageType.ProductListPage, PageType.ProductDetailsPage]
      });
   }

   // Label: Økologisk - Prio: 3 (#c40076)
   const lowercaseCertifications = product.certifications?.map(toLower) ?? [];
   if (includes(lowercaseCertifications, "eu_okologi") || includes(lowercaseCertifications, "debio")) {
      labels.push({
         text: "Økologisk",
         color: "green",
         position: "top-left",
         page: [PageType.ProductListPage, PageType.ProductDetailsPage]
      });
   }

   // Label: Egne frister (for minitanker) - Prio: 5 (#4d70bc)
   if (product.miniTank) {
      labels.push({
         text: "Egne frister",
         color: "gray",
         position: "bottom-left",
         page: [PageType.ProductListPage, PageType.ProductDetailsPage]
      });
   }

   // Label: Any labels defined in Plytix will be added here
   if (product.manuallabels) {
      for (const manualLabel of product.manuallabels) {
         const labelParts = manualLabel.split("|").map((part) => part.trim());

         const text = labelParts[0];
         if (!text) {
            continue;
         }
         let color: Label["color"] = "gray";
         let position: Label["position"] = "top-left";

         if (labelParts.length >= 2) {
            if (isLabelColor(labelParts[1])) {
               color = labelParts[1];
            }
         }

         if (labelParts.length === 3) {
            if (isLabelPosition(labelParts[2])) {
               position = labelParts[2];
            }
         }

         labels.push({
            text,
            color,
            position,
            page: [PageType.ProductListPage, PageType.ProductDetailsPage]
         });
      }
   }

   // Label: Utgått - Prio: 3 - Show after product is expired
   // OBS! Moved to manual control in Plytix for now.

   return labels;
};

/**
 * Returns a boolean indicating whether the product matches the active filters.
 */
export const matchesFilter = (product: Product, activeFilters: Filters): boolean => {
   return every(activeFilters, (selectedValues, field) => {
      // Type-guards to make sure field name sent to filter function are "filterable"
      if (!hasKey(product, field) || !isFilterAttribute(field)) {
         return false;
      }
      if (!selectedValues?.length) {
         return true;
      }
      const productValue = product[field];
      return some(selectedValues, (selectedValue) =>
         isArray(productValue) ? includes(productValue, selectedValue) : productValue === selectedValue
      );
   });
};

const isMultiValueField = (field: LegalFilterKey) =>
   ["certifications", "tastes", "tastegroups", "allergyinfo", "brands", "manuallabels", "manualshortcuts"].includes(field);
/**
 * Creates a list of unique values for a specific product attribute given the name of the field and a list of products. Used to build one filter
 * for each filterable attribute.
 *
 * @returns Object with field key, field name and a object with one key for each avaialble value and a list of SKUs that have this value
 */
export const buildFilter = (
   products: Product[] | undefined,
   field: LegalFilterKey,
   name: string,
   filterOptions: FilterOptionsByField
): FilterSettings | null => {
   if (isUndefined(products)) {
      return null;
   }

   // Remove products that dont have this field set or has an empty array for this field
   const validProductsForFilters = products.filter((p) => !isNil(p[field]) && (p[field]?.length ?? 0) > 0);

   // If a field given is a field that can have multiple values, we split the product into multiple products, one for each value
   // in array (for instance, one products with two certifications turns into two separate products in this chain)
   // The filter excludes values that are falsey
   const skuAndFieldValues = validProductsForFilters
      .flatMap((p) =>
         isMultiValueField(field)
            ? ((p[field] as string[]) ?? []).map((c) => ({ sku: p.sku, value: c }))
            : [{ sku: p.sku, value: p[field] }]
      )
      .filter((skuAndFieldValue) => skuAndFieldValue.value);

   // Group all products based on their value in field
   const groupedByValue = groupBy(skuAndFieldValues, "value");

   const knownValues = filterOptions[field];

   const values = knownValues ? knownValues.filter((value) => value in groupedByValue) : Object.keys(groupedByValue);

   const groupedByFieldValue: { [fieldValue: string]: string[] } = mapValues(groupedByValue, "sku");

   if (keys(groupedByFieldValue).length === 0) {
      return null;
   }

   const optional = field === "customertype" ? { radioDefault: "Isbar" } : {};

   return {
      field,
      name,
      values,
      ...optional
   };
};

export const getRelevantFilterSettings = (
   fullSettings: FilterSettings[],
   effectiveFilters: Filters,
   products: Product[],
   shortcutFilter: ShortcutFilter | "",
   filterOptions: FilterOptionsByField,
   manualShortcuts: ManualShortcut[]
): FilterSettings[] => {
   const relevantFilterSettings = fullSettings
      .map((settings) => {
         if (settings.radioDefault) {
            return settings;
         }
         let testFilters = { ...effectiveFilters };
         const filterForSettings = effectiveFilters[settings.field];
         if (filterForSettings) {
            const { [settings.field]: _, ...otherFilters } = effectiveFilters;
            testFilters = otherFilters;
         }
         const productsFilteredOnOtherFilters = products
            .filter((product) => matchesFilter(product, testFilters))
            .filter((product) => !shortcutFilter || checkShortcutFilter(manualShortcuts, product, shortcutFilter));

         const filterSettings = buildFilter(productsFilteredOnOtherFilters, settings.field, settings.name, filterOptions);
         if (filterSettings && shortcutFilter === "laktosefri" && filterSettings.values.includes("Laktosefri")) {
            filterSettings.values = settings.values.filter((v) => v !== "Laktosefri");
            if (!filterSettings.values.length) {
               return null;
            }
         }
         return filterSettings;
      })
      .filter(
         (f) => (f && f.values.length > 0 && f.values.length < 11) || f?.field === "tastegroups" || f?.field === "brands"
      ) as FilterSettings[];

   const hasTastes = relevantFilterSettings.some((filterSetting) => filterSetting.field === "tastes");
   const hasTastGroupsEffectiveFilter = (effectiveFilters.tastegroups?.length || 0) > 0;

   return legalFilterKeys
      .map((key) => relevantFilterSettings.find((filter) => filter.field === key))
      .filter(
         (filter) =>
            filter &&
            !(hasTastes && !hasTastGroupsEffectiveFilter && filter.field === "tastegroups") &&
            !(hasTastGroupsEffectiveFilter && filter.field === "tastes")
      ) as FilterSettings[];
};

export const hasDiscount = (product: Product): boolean => product.priceBeforeDiscount !== product.price;

export const isDistributionPack = (unit: OrderUnit): boolean => includes([ORDER_UNIT.D, ORDER_UNIT.DFP], unit);

const nbsp = String.fromCodePoint(0x00a0);
const _half = String.fromCodePoint(0x00bd); // ½

/**
 * Trims the input name and replaces any whitespace before units (kg, g, l, ml)
 * at the end of the string with a non-breaking space to prevent line breaks.
 * For example, "Milk 1 l" becomes "Milk 1<nbsp>l".
 */
export const fixDanglingUnits = (name: string) => {
   return name ? name.trim().replace(/\s+(kg|g|l|ml|dl|liter|%)(\s|$)/gi, `${nbsp}$1$2`) : name;
};

export const findPrioritizationScore = (labels: LabelText[], prioritizedLabelsList: LabelText[]) => {
   let score = 0;
   for (const label of labels) {
      const index = prioritizedLabelsList.indexOf(label);
      if (index !== -1) {
         score += 2 ** (prioritizedLabelsList.length - index);
      }
   }
   return score;
};

export function prioritizeProductResults(products: Product[] | undefined, prioritizedLabelsList: LabelText[]): Product[] {
   const safeProducts = products ?? [];

   const withScore = safeProducts.map((p, idx) => ({
      p,
      score: findPrioritizationScore(p.labels?.map((label) => label.text) ?? [], prioritizedLabelsList),
      idx
   }));
   withScore.sort((a, b) => b.score - a.score || a.idx - b.idx);
   return withScore.map(({ p }) => p);
}

export const filter = (products: Product[], filters: Filters) => {
   return products.filter((p) => matchesFilter(p, filters));
};

export const shortcutFilterNames: Record<ShortcutFilter, string> = {
   nyheter: "Nyheter",
   okologisk: "Økologisk",
   avtalt_sortiment: "Avtalt sortiment",
   laktosefri: "Laktosefri",
   siste_sjanse: "Siste sjanse",
   rabatter: "Rabatter"
};

const shortcutNameToKey = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, "_") as ShortcutFilter;

export const arrayOrUndefined = (arr?: string[]) => (arr?.length ? arr : undefined);

const skusWithShortcut = (products: Product[], shortcutName: string) => {
   return products
      .filter((p) => p.manualshortcuts?.some((shortcutOnProduct) => shortcutOnProduct === shortcutName))
      .map((p) => p.sku);
};

const buildManualShortcut = (products: Product[], shortcutOrder: string[], shortcutName: string): ManualShortcut => {
   const order = shortcutOrder.indexOf(shortcutName);
   return {
      key: shortcutNameToKey(shortcutName),
      name: shortcutName,
      skus: skusWithShortcut(products, shortcutName),
      order: order === -1 ? Number.MAX_SAFE_INTEGER : order
   };
};

export const buildManualShortcuts = (products: Product[], shortcutOrder: string[]): ManualShortcut[] => {
   const shortcutsInProducts = products
      .flatMap((p) => p.manualshortcuts)
      .filter((shortcut): shortcut is string => Boolean(shortcut));

   const manualShortcuts = uniq(shortcutsInProducts).map((shortcutName) =>
      buildManualShortcut(products, shortcutOrder, shortcutName)
   );

   return sortBy(manualShortcuts, "order");
};

export const isManualShortcut = (manualShortcuts: ManualShortcut[], key: string) =>
   manualShortcuts.some((msc) => msc.key === key);

export const getShortcutName = (manualShortcuts: ManualShortcut[], key: string) => {
   if (hasKey(shortcutFilterNames, key)) {
      return shortcutFilterNames[key];
   }

   if (isManualShortcut(manualShortcuts, key)) {
      return manualShortcuts.find((msc) => msc.key === key)?.name || "";
   }

   return "Ukjent snarvei";
};

export const checkShortcutFilter = (manualShortcuts: ManualShortcut[], product: Product, filter: ShortcutFilter): boolean => {
   if (isManualShortcut(manualShortcuts, filter)) {
      const manualShortcut = manualShortcuts.find((msc) => msc.key === filter);
      if (manualShortcut) {
         return manualShortcut.skus.includes(product.sku);
      }
   }

   switch (filter) {
      case "okologisk":
         return (
            includes((product.certifications ?? []).map(toLower), "eu_okologi") ||
            includes((product.certifications ?? []).map(toLower), "debio")
         );
      case "avtalt_sortiment":
         return product.inAgreementAssortment === true;
      case "laktosefri":
         return includes(product.allergyinfo, "Laktosefri");
      case "siste_sjanse":
         return shouldShowExpiryNotice(product);
      case "rabatter":
         return product.hasPromotion || hasDiscount(product);
      default:
         return false;
   }
};

const scoresByMatchType = {
   exactMatch: 500,
   startsWith: 200,
   wholeWord: 100,
   contains: 50
} as const;

type TextMatchScoreType = keyof typeof scoresByMatchType;
type MatchableProperty = keyof Pick<Product, "name" | "sku" | "epdNumber">;

const propertyMatchTypes: Record<MatchableProperty, TextMatchScoreType[]> = {
   name: ["exactMatch", "wholeWord", "startsWith", "contains"],
   sku: ["exactMatch", "startsWith", "contains"],
   epdNumber: ["exactMatch", "startsWith", "contains"]
};

export const textMatchScore = (product: Product | undefined, text: string): number => {
   if (product) {
      if (text) {
         let totalScore = 0;
         const wholeWordRegExp = new RegExp(`\\b${text.replace(/[^a-zæøå0-9]/g, "")}\\b`, "gi");

         for (const property of keys(propertyMatchTypes) as MatchableProperty[]) {
            const propertyValue = product[property]?.replace(/®/g, "").replace(/\s/g, " "); // Remove registered trademark symbol
            if (propertyValue) {
               for (const matchType of propertyMatchTypes[property]) {
                  let isMatch = false,
                     match = null;
                  switch (matchType) {
                     case "exactMatch":
                        isMatch = propertyValue.toLowerCase() === text.toLowerCase();
                        break;
                     case "startsWith":
                        isMatch = propertyValue.toLowerCase().startsWith(text.toLowerCase());
                        break;
                     case "wholeWord":
                        match = propertyValue.match(wholeWordRegExp);
                        if (match && match.length > 0) {
                           totalScore += match.length;
                           isMatch = true;
                        }
                        break;

                     case "contains":
                        isMatch = propertyValue.toLowerCase().includes(text.toLowerCase());
                        break;
                  }
                  if (isMatch) {
                     totalScore += scoresByMatchType[matchType];
                  }
               }
            }
         }
         return totalScore;
      }
   }
   return 0;
};
